import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';

// ========== PROPOSAL TEMPLATES (Organization-Scoped) ==========

export const getTemplates = async (organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // If no organizationId provided, try to get from user's current organization
    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    const allTemplates = [];

    // Get organization templates if in an organization
    if (orgId) {
      const templatesRef = collection(db, 'organizations', orgId, 'proposal-templates');
      const q = query(templatesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        allTemplates.push({ 
          id: doc.id, 
          ...doc.data(),
          isOrganizationTemplate: true,
          scope: 'organization'
        });
      });
      
      console.log(`Loaded ${querySnapshot.size} organization templates`);
    }

    // ALWAYS get personal templates as well
    const personalTemplatesRef = collection(db, 'users', userId, 'proposal-templates');
    const personalQuery = query(personalTemplatesRef, orderBy('createdAt', 'desc'));
    const personalSnapshot = await getDocs(personalQuery);
    
    personalSnapshot.forEach((doc) => {
      allTemplates.push({ 
        id: doc.id, 
        ...doc.data(),
        isOrganizationTemplate: false,
        scope: 'personal'
      });
    });
    
    console.log(`Loaded ${personalSnapshot.size} personal templates`);
    console.log(`Total templates: ${allTemplates.length}`);

    return allTemplates;
  } catch (error) {
    console.error('Error getting templates:', error);
    throw error;
  }
};

export const addTemplate = async (templateData, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // If in an organization, add to organization templates
    if (orgId) {
      const templatesRef = collection(db, 'organizations', orgId, 'proposal-templates');
      const docRef = await addDoc(templatesRef, {
        ...templateData,
        createdBy: userId,
        organizationId: orgId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    }

    // Otherwise, add to personal templates
    const personalTemplatesRef = collection(db, 'users', userId, 'proposal-templates');
    const docRef = await addDoc(personalTemplatesRef, {
      ...templateData,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding template:', error);
    throw error;
  }
};

export const updateTemplate = async (templateId, updates, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // Update organization template
    if (orgId) {
      const templateRef = doc(db, 'organizations', orgId, 'proposal-templates', templateId);
      await updateDoc(templateRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    }

    // Update personal template
    const templateRef = doc(db, 'users', userId, 'proposal-templates', templateId);
    await updateDoc(templateRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

export const deleteTemplate = async (templateId, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // Delete organization template
    if (orgId) {
      await deleteDoc(doc(db, 'organizations', orgId, 'proposal-templates', templateId));
      return true;
    }

    // Delete personal template
    await deleteDoc(doc(db, 'users', userId, 'proposal-templates', templateId));
    return true;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

// ========== PROJECT EXAMPLES (Organization-Scoped) ==========

export const getProjectExamples = async (organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    const allExamples = [];

    // Get organization examples if in an organization
    if (orgId) {
      const examplesRef = collection(db, 'organizations', orgId, 'project-examples');
      const q = query(examplesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        allExamples.push({ 
          id: doc.id, 
          ...doc.data(),
          isOrganizationExample: true,
          scope: 'organization'
        });
      });
      
      console.log(`Loaded ${querySnapshot.size} organization examples`);
    }

    // ALWAYS get personal examples as well
    const personalExamplesRef = collection(db, 'users', userId, 'project-examples');
    const personalQuery = query(personalExamplesRef, orderBy('createdAt', 'desc'));
    const personalSnapshot = await getDocs(personalQuery);
    
    personalSnapshot.forEach((doc) => {
      allExamples.push({ 
        id: doc.id, 
        ...doc.data(),
        isOrganizationExample: false,
        scope: 'personal'
      });
    });
    
    console.log(`Loaded ${personalSnapshot.size} personal examples`);
    console.log(`Total examples: ${allExamples.length}`);

    return allExamples;
  } catch (error) {
    console.error('Error getting project examples:', error);
    throw error;
  }
};

export const addProjectExample = async (exampleData, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // If in an organization, add to organization examples
    if (orgId) {
      const examplesRef = collection(db, 'organizations', orgId, 'project-examples');
      const docRef = await addDoc(examplesRef, {
        ...exampleData,
        createdBy: userId,
        organizationId: orgId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    }

    // Otherwise, add to personal examples
    const personalExamplesRef = collection(db, 'users', userId, 'project-examples');
    const docRef = await addDoc(personalExamplesRef, {
      ...exampleData,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding project example:', error);
    throw error;
  }
};

export const updateProjectExample = async (exampleId, updates, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // Update organization example
    if (orgId) {
      const exampleRef = doc(db, 'organizations', orgId, 'project-examples', exampleId);
      await updateDoc(exampleRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    }

    // Update personal example
    const exampleRef = doc(db, 'users', userId, 'project-examples', exampleId);
    await updateDoc(exampleRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating project example:', error);
    throw error;
  }
};

export const deleteProjectExample = async (exampleId, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // Delete organization example
    if (orgId) {
      await deleteDoc(doc(db, 'organizations', orgId, 'project-examples', exampleId));
      return true;
    }

    // Delete personal example
    await deleteDoc(doc(db, 'users', userId, 'project-examples', exampleId));
    return true;
  } catch (error) {
    console.error('Error deleting project example:', error);
    throw error;
  }
};

// ========== USER PREFERENCES ==========

export const getUserPreferences = async () => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    const prefsRef = doc(db, 'users', userId, 'settings', 'preferences');
    const docSnap = await getDoc(prefsRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Return default preferences
      return {
        strictAdherenceDefault: false,
      };
    }
  } catch (error) {
    console.error('Error getting user preferences:', error);
    throw error;
  }
};

export const updateUserPreferences = async (preferences) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    const prefsRef = doc(db, 'users', userId, 'settings', 'preferences');
    await setDoc(prefsRef, {
      ...preferences,
      updatedAt: serverTimestamp()
    }, { merge: true });

    return true;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

// ========== TEMPLATE USAGE TRACKING ==========

export const trackTemplateUsage = async (templateId, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.currentOrganization;
    }

    // Increment usage count in template document
    if (orgId) {
      const templateRef = doc(db, 'organizations', orgId, 'proposal-templates', templateId);
      const templateDoc = await getDoc(templateRef);
      
      if (templateDoc.exists()) {
        await updateDoc(templateRef, {
          usageCount: (templateDoc.data().usageCount || 0) + 1,
          lastUsed: serverTimestamp()
        });
      }
    } else {
      const templateRef = doc(db, 'users', userId, 'proposal-templates', templateId);
      const templateDoc = await getDoc(templateRef);
      
      if (templateDoc.exists()) {
        await updateDoc(templateRef, {
          usageCount: (templateDoc.data().usageCount || 0) + 1,
          lastUsed: serverTimestamp()
        });
      }
    }

    return true;
  } catch (error) {
    console.error('Error tracking template usage:', error);
    // Don't throw - tracking shouldn't break the flow
    return false;
  }
};

// ========== SEED DATA (One-time use) ==========

export const seedHardcodedExamples = async (hardcodedExamples, forceReseed = false) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Check if user already has examples
    const existing = await getProjectExamples();
    if (existing.length > 0 && !forceReseed) {
      console.log('User already has examples, skipping seed');
      return false;
    }

    // If reseeding, delete all existing examples first
    if (forceReseed && existing.length > 0) {
      console.log('Force reseed: Deleting existing examples...');
      const deletePromises = existing.map(ex => deleteProjectExample(ex.id));
      await Promise.all(deletePromises);
      console.log('Deleted all existing examples');
    }

    // Add all hardcoded examples with ALL fields
    const promises = hardcodedExamples.map(example => 
      addProjectExample({
        title: example.title,
        technologies: example.technologies || '',
        description: example.description,
        tags: example.tags,
        category: example.category,
        link: example.link || '',
        caseStudy: example.caseStudy || ''
      })
    );

    await Promise.all(promises);
    console.log(`Successfully seeded ${hardcodedExamples.length} examples with technologies and case studies!`);
    return true;
  } catch (error) {
    console.error('Error seeding examples:', error);
    throw error;
  }
};

// ========== PARTNER LOGOS (Site Management) ==========

export const getPartnerLogos = async () => {
  try {
    const logosRef = collection(db, 'siteSettings');
    const q = query(logosRef, where('type', '==', 'partnerLogo'));
    const querySnapshot = await getDocs(q);
    
    const logos = [];
    querySnapshot.forEach((doc) => {
      logos.push({ id: doc.id, ...doc.data() });
    });

    // Sort in memory by order
    logos.sort((a, b) => (a.order || 0) - (b.order || 0));

    return logos;
  } catch (error) {
    console.error('Error getting partner logos:', error);
    return [];
  }
};

export const addPartnerLogo = async (logoData) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    const logosRef = collection(db, 'siteSettings');
    const docRef = await addDoc(logosRef, {
      ...logoData,
      type: 'partnerLogo',
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding partner logo:', error);
    throw error;
  }
};

export const updatePartnerLogo = async (logoId, updates) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    const logoRef = doc(db, 'siteSettings', logoId);
    await updateDoc(logoRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error updating partner logo:', error);
    throw error;
  }
};

export const deletePartnerLogo = async (logoId) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    await deleteDoc(doc(db, 'siteSettings', logoId));
    return true;
  } catch (error) {
    console.error('Error deleting partner logo:', error);
    throw error;
  }
};

export const getCarouselSettings = async () => {
  try {
    const settingsRef = doc(db, 'siteSettings', 'carouselSettings');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return settingsDoc.data();
    }
    
    // Default values: 40s speed, 120px logo size, 60px spacing
    return { speed: 40, logoSize: 120, logoSpacing: 60 };
  } catch (error) {
    console.error('Error getting carousel settings:', error);
    return { speed: 40, logoSize: 120, logoSpacing: 60 };
  }
};

export const updateCarouselSettings = async (settings) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    const settingsRef = doc(db, 'siteSettings', 'carouselSettings');
    await setDoc(settingsRef, {
      ...settings,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating carousel settings:', error);
    throw error;
  }
};

// ========== CONTRACT TEMPLATES ==========

/**
 * Seed a default contract template (dev only)
 */
export const seedContractTemplate = async (templateData) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Check if user already has this template
    const existing = await getContractTemplates();
    const hasTemplate = existing.some(t => t.name === templateData.name);
    
    if (hasTemplate) {
      console.log('Contract template already exists');
      return false;
    }

    // Add the template
    await addContractTemplate(templateData);
    console.log('✅ Seeded contract template successfully');
    return true;
  } catch (error) {
    console.error('Error seeding contract template:', error);
    throw error;
  }
};

/**
 * Get contract templates (both organization and personal)
 */
export const getContractTemplates = async (organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Get user's organizationId from profile if not provided
    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.organizationId;
    }

    const allTemplates = [];

    // Fetch organization contract templates if user belongs to an org
    if (orgId) {
      const orgQuery = query(
        collection(db, 'organizations', orgId, 'contract-templates'),
        orderBy('createdAt', 'desc')
      );
      const orgSnapshot = await getDocs(orgQuery);
      orgSnapshot.forEach((doc) => {
        allTemplates.push({
          id: doc.id,
          ...doc.data(),
          isOrganizationTemplate: true,
          scope: 'organization'
        });
      });
    }

    // Always fetch personal contract templates
    const personalQuery = query(
      collection(db, 'users', userId, 'contract-templates'),
      orderBy('createdAt', 'desc')
    );
    const personalSnapshot = await getDocs(personalQuery);
    personalSnapshot.forEach((doc) => {
      allTemplates.push({
        id: doc.id,
        ...doc.data(),
        isOrganizationTemplate: false,
        scope: 'personal'
      });
    });

    return allTemplates;
  } catch (error) {
    console.error('Error getting contract templates:', error);
    throw error;
  }
};

/**
 * Add a new contract template
 */
export const addContractTemplate = async (templateData, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Get user's organizationId from profile if not provided
    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.organizationId;
    }

    const templateWithMeta = {
      ...templateData,
      createdAt: serverTimestamp(),
      createdBy: userId,
      usageCount: 0
    };

    let docRef;
    if (templateData.scope === 'organization' && orgId) {
      // Add to organization collection
      docRef = await addDoc(
        collection(db, 'organizations', orgId, 'contract-templates'),
        templateWithMeta
      );
    } else {
      // Add to personal collection
      docRef = await addDoc(
        collection(db, 'users', userId, 'contract-templates'),
        templateWithMeta
      );
    }

    return docRef.id;
  } catch (error) {
    console.error('Error adding contract template:', error);
    throw error;
  }
};

/**
 * Update a contract template
 */
export const updateContractTemplate = async (templateId, updates, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Get user's organizationId from profile if not provided
    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.organizationId;
    }

    let templateRef;
    if (updates.scope === 'organization' && orgId) {
      templateRef = doc(db, 'organizations', orgId, 'contract-templates', templateId);
    } else {
      templateRef = doc(db, 'users', userId, 'contract-templates', templateId);
    }

    await updateDoc(templateRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating contract template:', error);
    throw error;
  }
};

/**
 * Delete a contract template
 */
export const deleteContractTemplate = async (templateId, organizationId = null) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User not authenticated');

    // Get user's organizationId from profile if not provided
    let orgId = organizationId;
    if (!orgId) {
      const userDoc = await getDoc(doc(db, 'users', userId));
      orgId = userDoc.data()?.organizationId;
    }

    // Try deleting from personal first
    const personalRef = doc(db, 'users', userId, 'contract-templates', templateId);
    try {
      await deleteDoc(personalRef);
      return;
    } catch (e) {
      // If not in personal, try organization
      if (orgId) {
        const orgRef = doc(db, 'organizations', orgId, 'contract-templates', templateId);
        await deleteDoc(orgRef);
      } else {
        throw e;
      }
    }
  } catch (error) {
    console.error('Error deleting contract template:', error);
    throw error;
  }
};
